// Centralized maintenance check with bounded retries and outcome classification.
import { userActions } from "../actions";
import { apiService } from "../services";
import { store } from "../store/store";
import {
  is503ProbeError,
  isCanceledProbeError,
  isMaintenanceProbeHttp503,
  isMaintenancePageState,
  jitterMs,
  MAINTENANCE_OUTCOME,
  MAINTENANCE_PROBE_ATTEMPTS,
  MAINTENANCE_PROBE_DELAYS_MS,
  isPortalApiRecentlyReachable,
  markMaintenanceHttp503Observed,
  normalize503Payload,
  normalizeUnreachablePayload,
  sleepMs,
  wasMaintenanceHttp503Observed,
} from "./maintenanceProbe";

// UI code imports maintenance state from here. The pure helpers live in
// maintenanceProbe.js so client.js and api.service.js can use them without the
// client -> maintenance -> api.service -> client import cycle.
export {
  CONNECTIVITY_BANNER_BODY,
  isConnectivityBannerState,
  isMaintenancePageState,
  MAINTENANCE_OUTCOME,
  normalizeUnreachablePayload,
} from "./maintenanceProbe";

let activeCheckGeneration = 0;

const isLoginOrLogoutHash = () => {
  try {
    const hash = String(window?.location?.hash || "");
    return hash.includes("login") || hash.includes("logout");
  } catch (_e) {
    return false;
  }
};

/**
 * Maintenance GET may return 503 in DevTools while axios sees ERR_NETWORK (no CORS).
 * Use portal.api reachability on authenticated routes: if nothing on portal.api
 * succeeded recently, show the maintenance page. Login/logout shells never had a
 * successful portal.api call — show the connectivity banner unless HTTP 503 was seen.
 */
const inferMaintenancePageAfterUnreachableProbe = async () => {
  if (wasMaintenanceHttp503Observed()) {
    return { response: { status: 503, data: null } };
  }

  if (!isPortalApiRecentlyReachable()) {
    if (isLoginOrLogoutHash()) {
      return null;
    }
    return { response: { status: 503, data: null } };
  }

  try {
    await apiService.checkHealthProbe();
    return null;
  } catch (error) {
    if (is503ProbeError(error)) {
      return error;
    }
    if (!error?.response) {
      return { response: { status: 503, data: null } };
    }
    return null;
  }
};

const setAppHash = (hash) => {
  const next = hash.startsWith("#") ? hash : `#${hash}`;
  try {
    if (typeof window.history?.replaceState === "function") {
      window.history.replaceState(null, "", next);
      return;
    }
  } catch (_e) {
    /* fall through */
  }
  try {
    window.location.href = next;
  } catch (_e2) {
    /* ignore */
  }
};

const navigateToMaintenancePage = () => {
  setAppHash("#/maintenance");
};

const leaveMaintenanceHashIfNeeded = () => {
  try {
    const current = String(window?.location?.hash || "");
    if (/maintenance/i.test(current)) {
      setAppHash("#/");
    }
  } catch (_e) {
    /* ignore */
  }
};

const dispatchHealthy = (dispatch, generation) => {
  if (generation !== activeCheckGeneration) {
    return MAINTENANCE_OUTCOME.CANCELED;
  }
  dispatch(userActions.maintenanceMessage("CLEAR_MAINTENANCE_MESSAGE"));
  leaveMaintenanceHashIfNeeded();
  return MAINTENANCE_OUTCOME.HEALTHY;
};

const dispatch503Page = (dispatch, generation, error) => {
  markMaintenanceHttp503Observed("dispatch503Page");
  const payload = normalize503Payload(
    error && error.response && error.response.data,
  );
  dispatch(userActions.maintenanceMessage("MAINTENANCE_MESSAGE", payload));
  navigateToMaintenancePage();
  return MAINTENANCE_OUTCOME.PAGE;
};

const dispatchUnreachableBanner = (dispatch, generation) => {
  if (generation !== activeCheckGeneration) {
    return MAINTENANCE_OUTCOME.CANCELED;
  }

  const maintenanceState = store.getState().maintenanceMessage;
  if (
    wasMaintenanceHttp503Observed() ||
    isMaintenancePageState(maintenanceState)
  ) {
    return dispatch503Page(dispatch, generation, {
      response: { status: 503, data: null },
    });
  }

  dispatch(
    userActions.maintenanceMessage(
      "MAINTENANCE_MESSAGE",
      normalizeUnreachablePayload(),
    ),
  );
  return MAINTENANCE_OUTCOME.BANNER;
};

/** Client interceptor may already have set page mode on a visible HTTP 503. */
const finishAsMaintenancePageIfAlreadyDispatched = () => {
  const maintenanceState = store.getState().maintenanceMessage;
  if (!isMaintenancePageState(maintenanceState)) {
    return null;
  }
  navigateToMaintenancePage();
  return MAINTENANCE_OUTCOME.PAGE;
};

/**
 * Probe portal maintenance endpoint with bounded retries.
 *
 * @returns {Promise<import('./maintenanceProbe').MaintenanceCheckOutcome>}
 *   `true` healthy, `false` 503 maintenance page, `'banner'` connectivity
 *   warning, `null` canceled/superseded.
 */
export async function checkMaintenanceWithRetry(dispatch) {
  const generation = ++activeCheckGeneration;

  for (
    let attempt = 0;
    attempt < MAINTENANCE_PROBE_ATTEMPTS;
    attempt += 1
  ) {
    if (generation !== activeCheckGeneration) {
      return MAINTENANCE_OUTCOME.CANCELED;
    }

    if (attempt > 0) {
      const delayBase =
        MAINTENANCE_PROBE_DELAYS_MS[attempt - 1] ||
        MAINTENANCE_PROBE_DELAYS_MS[
          MAINTENANCE_PROBE_DELAYS_MS.length - 1
        ];
      await sleepMs(jitterMs(delayBase));
    }

    if (generation !== activeCheckGeneration) {
      return MAINTENANCE_OUTCOME.CANCELED;
    }

    try {
      const status = await apiService.checkMaintenance();
      if (status === 200) {
        return dispatchHealthy(dispatch, generation);
      }
      if (isMaintenanceProbeHttp503(status)) {
        markMaintenanceHttp503Observed("check.attempt.resolved503", {
          attempt,
        });
        return dispatch503Page(dispatch, generation, {
          response: { status: 503, data: null },
        });
      }
    } catch (error) {
      if (generation !== activeCheckGeneration) {
        return MAINTENANCE_OUTCOME.CANCELED;
      }
      if (is503ProbeError(error)) {
        markMaintenanceHttp503Observed("check.attempt.rejected503", {
          attempt,
        });
        return dispatch503Page(dispatch, generation, error);
      }
      if (isCanceledProbeError(error)) {
        return MAINTENANCE_OUTCOME.CANCELED;
      }
    }
  }

  if (generation !== activeCheckGeneration) {
    return MAINTENANCE_OUTCOME.CANCELED;
  }

  const alreadyPage = finishAsMaintenancePageIfAlreadyDispatched();
  if (alreadyPage !== null) {
    return alreadyPage;
  }

  const outageError = await inferMaintenancePageAfterUnreachableProbe();
  if (outageError) {
    return dispatch503Page(dispatch, generation, outageError);
  }

  return dispatchUnreachableBanner(dispatch, generation);
}
